import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { countActiveFilters } from '@/lib/blog';
import { cn } from '@/lib/utils';
import { BlogCategory, Tag } from '@/types/model';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { useState } from 'react';

interface BlogFiltersProps {
  categories: BlogCategory[];
  tags: Tag[];
  currentFilters: {
    category?: string;
    tags?: string[];
  };
  onApplyFilters: (filters: { category?: string; tags?: string[] }) => void;
}

interface FilterContentProps {
  categories: BlogCategory[];
  tags: Tag[];
  selectedCategory: string;
  selectedTags: string[];
  onCategoryChange: (value: string) => void;
  onTagToggle: (slug: string) => void;
  onClear: () => void;
  onApply: () => void;
}

function FilterContent({ categories, tags, selectedCategory, selectedTags, onCategoryChange, onTagToggle, onClear, onApply }: FilterContentProps) {
  const totalPostCount = categories.reduce((sum, cat) => sum + (cat.published_posts_count || 0), 0);
  const hasSelections = !!selectedCategory || selectedTags.length > 0;

  return (
    <div className="flex h-full flex-col">
      <ScrollArea className="flex-1">
        <div className="space-y-5 px-4 py-4">
          {/* Categories */}
          <div>
            <p className="text-muted-foreground mb-3 text-xs font-semibold tracking-widest uppercase">Danh mục</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => onCategoryChange('')}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all duration-150',
                  selectedCategory === ''
                    ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                    : 'border-border bg-background text-foreground hover:border-primary/50 hover:bg-primary/5',
                )}
              >
                Tất cả
                <span className={cn('text-xs tabular-nums', selectedCategory === '' ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                  {totalPostCount}
                </span>
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => onCategoryChange(category.slug)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all duration-150',
                    selectedCategory === category.slug
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'border-border bg-background text-foreground hover:border-primary/50 hover:bg-primary/5',
                  )}
                >
                  {category.name}
                  <span
                    className={cn(
                      'text-xs tabular-nums',
                      selectedCategory === category.slug ? 'text-primary-foreground/70' : 'text-muted-foreground',
                    )}
                  >
                    {category.published_posts_count || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <>
              <Separator />
              <div>
                <p className="text-muted-foreground mb-3 text-xs font-semibold tracking-widest uppercase">Thẻ</p>
                <div className="flex flex-wrap gap-2">
                  {tags.slice(0, 24).map((tag) => {
                    const isSelected = selectedTags.includes(tag.slug);
                    return (
                      <button
                        key={tag.id}
                        onClick={() => onTagToggle(tag.slug)}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all duration-150',
                          isSelected
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-background text-foreground hover:border-primary/40 hover:bg-primary/5',
                        )}
                      >
                        {isSelected && <span className="bg-primary h-1.5 w-1.5 rounded-full" />}
                        {tag.name}
                        <span className={cn('text-xs tabular-nums', isSelected ? 'text-primary/70' : 'text-muted-foreground')}>
                          {tag.published_posts?.length || 0}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="flex items-center gap-2 border-t px-4 py-3">
        <Button variant="ghost" size="sm" onClick={onClear} disabled={!hasSelections} className="text-muted-foreground gap-1.5">
          <X className="h-3.5 w-3.5" />
          Xóa bộ lọc
        </Button>
        <Button onClick={onApply} size="sm" className="ml-auto px-6">
          Áp dụng
        </Button>
      </div>
    </div>
  );
}

export function BlogFilters({ categories, tags, currentFilters, onApplyFilters }: BlogFiltersProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(currentFilters.category || '');
  const [selectedTags, setSelectedTags] = useState<string[]>(currentFilters.tags || []);

  const activeFiltersCount = countActiveFilters(currentFilters);

  const toggleTag = (tagSlug: string) => {
    setSelectedTags((prev) => (prev.includes(tagSlug) ? prev.filter((t) => t !== tagSlug) : [...prev, tagSlug]));
  };

  const handleClearFilters = () => {
    setSelectedCategory('');
    setSelectedTags([]);
  };

  const handleApplyFilters = () => {
    onApplyFilters({
      category: selectedCategory || undefined,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
    });
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setSelectedCategory(currentFilters.category || '');
      setSelectedTags(currentFilters.tags || []);
    }
  };

  const triggerButton = (
    <div className="border-border bg-background hover:border-primary/50 relative inline-flex w-fit cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-sm transition-all select-none hover:shadow-md">
      <SlidersHorizontal className="text-muted-foreground h-4 w-4" />
      <span>Lọc bài viết</span>
      {activeFiltersCount > 0 ? (
        <div className="bg-primary size-4 h-5 min-w-5 rounded-full! text-white">{activeFiltersCount}</div>
      ) : (
        <ChevronDown className={cn('text-muted-foreground h-4 w-4 transition-transform duration-200', open && 'rotate-180')} />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>
          <button className="focus:outline-none">{triggerButton}</button>
        </SheetTrigger>
        <SheetContent side="bottom" className="flex h-[82vh] flex-col rounded-t-2xl p-0">
          <SheetHeader className="border-b px-4 pt-4 pb-3">
            <SheetTitle className="text-base">Lọc bài viết</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-hidden">
            <FilterContent
              categories={categories}
              tags={tags}
              selectedCategory={selectedCategory}
              selectedTags={selectedTags}
              onCategoryChange={setSelectedCategory}
              onTagToggle={toggleTag}
              onClear={handleClearFilters}
              onApply={handleApplyFilters}
            />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button className="focus:outline-none">{triggerButton}</button>
      </PopoverTrigger>
      <PopoverContent align="center" sideOffset={8} className="w-[460px] overflow-hidden rounded-2xl border p-0 shadow-xl">
        {/* Popover header */}
        <div className="bg-muted/30 flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-semibold">Lọc bài viết</p>
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="rounded-full text-xs">
              {activeFiltersCount} đang lọc
            </Badge>
          )}
        </div>
        <div className="flex max-h-[440px] flex-col">
          <FilterContent
            categories={categories}
            tags={tags}
            selectedCategory={selectedCategory}
            selectedTags={selectedTags}
            onCategoryChange={setSelectedCategory}
            onTagToggle={toggleTag}
            onClear={handleClearFilters}
            onApply={handleApplyFilters}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
